import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, createAccount, inviteToAccount, listAccounts, type Account } from '../api';

export default function Accounts() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [inviteFor, setInviteFor] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const res = await listAccounts();
      setAccounts(res.accounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await createAccount(newName.trim());
      setNewName('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    }
  }

  if (loading) return <p>Loading…</p>;

  return (
    <div>
      <h2>Households</h2>
      {error && <p style={{ color: '#b00' }}>{error}</p>}

      {accounts.length === 0 ? (
        <p style={{ color: '#666' }}>You're not a member of any households yet.</p>
      ) : (
        <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
          {accounts.map((a) => (
            <li
              key={a.id}
              style={{
                border: '1px solid #ddd',
                borderRadius: 6,
                padding: '0.75rem 1rem',
                marginBottom: '0.5rem',
              }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div>
                  <strong>{a.name}</strong>
                  <span style={{ color: '#888', marginLeft: '0.5rem' }}>{a.role}</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <Link to={`/accounts/${encodeURIComponent(a.id)}/dashboard`}>View dashboard</Link>
                  <Link to={`/accounts/${encodeURIComponent(a.id)}/funds`}>View funds</Link>
                  <button onClick={() => setInviteFor(inviteFor === a.id ? null : a.id)}>
                    {inviteFor === a.id ? 'Cancel' : 'Invite member'}
                  </button>
                </div>
              </div>
              {inviteFor === a.id && (
                <InviteForm accountId={a.id} onDone={() => setInviteFor(null)} />
              )}
            </li>
          ))}
        </ul>
      )}

      <h3 style={{ marginTop: '2rem' }}>Create a new household</h3>
      <form onSubmit={onCreate} style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="e.g. Smith Family"
          style={{ flex: 1 }}
        />
        <button type="submit">Create</button>
      </form>
    </div>
  );
}

function InviteForm({ accountId, onDone }: { accountId: string; onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<{ kind: 'idle' | 'sent' | 'error'; msg?: string }>({
    kind: 'idle',
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus({ kind: 'idle' });
    try {
      await inviteToAccount(accountId, email);
      setStatus({ kind: 'sent' });
      setEmail('');
      onDone();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to invite';
      setStatus({ kind: 'error', msg });
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}
    >
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="user@example.com"
        required
        style={{ flex: 1 }}
      />
      <button type="submit">Send invite</button>
      {status.kind === 'error' && <span style={{ color: '#b00' }}>{status.msg}</span>}
    </form>
  );
}
